import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LANGUAGE } from '@bliks-io/contracts';
import { useProfile } from '@/api/queries/useProfile';
import { useOrgSettings } from '@/api/queries/useOrgSettings';

/**
 * Initializes i18n language based on user and organization preferences
 *
 * Priority order:
 * 1. User's personal language preference
 * 2. Organization's default language
 * 3. Browser/system language (from i18next LanguageDetector)
 * 4. Fallback to DEFAULT_LANGUAGE
 *
 * Runs once on mount and whenever user or organization language preferences change.
 *
 * Mounted per app shell: in SlugLayout (routes/$slug.tsx) above the
 * setup-screen early return so it also runs for setup screens, and in
 * PartnerLayout, which the partner area uses instead.
 */
export function useLanguageInitialization() {
  const { i18n } = useTranslation();
  const { data: user } = useProfile();
  const { data: settings } = useOrgSettings();

  useEffect(
    function syncI18nLanguage() {
      // Determine language based on priority
      const language =
        user?.language || settings?.defaultLanguage || i18n.language || DEFAULT_LANGUAGE;

      // Only change if different from current language to prevent unnecessary re-renders
      if (i18n.language !== language) {
        i18n.changeLanguage(language);
      }
    },
    [user?.language, settings?.defaultLanguage, i18n]
  );
}
